import { useState, useEffect, useCallback } from 'react';

// Helper function (moved from ClientOrderPanel)
const checkIfPersonalizationAvailable = (pages) => {
  if (!Array.isArray(pages)) return false;
  for (const page of pages) {
    // Check for personalize flag in elements
    const hasPersoFlag =
      (page.texts || []).some((t) => t.flags?.personalize) ||
      (page.images || []).some((im) => im.flags?.personalize) ||
      (page.svgs || []).some((sv) => sv.flags?.personalize);
    if (hasPersoFlag) return true;
    // Check for legacy personalization array (implicit personalization: true)
    if (Array.isArray(page.personalizacja)) return true;
  }
  return false;
};

export function useClientOrderData(backendUrl, publicKey, showPopup) {
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Stan dla początkowego ładowania panelu
  const [statuses, setStatuses] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(true); // Stan ładowania statusów
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  // Usunięto stany personalizationAvailability i personalizationLoading

  // Pobiera zamówienie
  const fetchOrder = useCallback(async () => {
    // Don't start loading if already loading to prevent loops in some cases
    // setIsLoading(true); // Start loading only if not already loading? Or always reset? Let's always reset for now.
    setIsLoading(true);
    try {
      const url = `${backendUrl}/client/orders/${publicKey}`;
      console.log("[useClientOrderData] fetchOrder => GET =>", url);
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(
          `Błąd pobierania zamówienia public (status=${res.status})`
        );
      }
      const data = await res.json();
      console.log("[useClientOrderData] fetchOrder => data=", data);
      setOrder(data);
    } catch (err) {
      console.error("[useClientOrderData] fetchOrder =>", err);
      if (showPopup) {
        showPopup({ title: "Błąd", message: "Nie udało się pobrać szczegółów zamówienia.", type: 'error' });
      }
    } finally {
      setIsLoading(false); // Zakończ ładowanie niezależnie od wyniku
    }
  }, [backendUrl, publicKey, showPopup]); // Added showPopup dependency

  // Pobiera zamówienie przy montowaniu i zmianie publicKey/backendUrl
  useEffect(() => {
    if (backendUrl && publicKey) {
      fetchOrder();
    }
  }, [fetchOrder, backendUrl, publicKey]); // Keep backendUrl and publicKey here for clarity

  // Pobiera listę statusów
  useEffect(() => {
    const fetchAllStatuses = async () => {
      setStatusesLoading(true); // Rozpocznij ładowanie statusów
      try {
        const resp = await fetch(`${backendUrl}/public/statuses`);
        if (!resp.ok) {
          throw new Error(
            "Błąd pobierania listy statusów (client), status=" + resp.status
          );
        }
        const data = await resp.json();
        setStatuses(data);
      } catch (err) {
        console.error("[useClientOrderData] fetchAllStatuses =>", err);
        // Optionally show error popup
        // if (showPopup) showPopup({ title: "Błąd", message: "Nie udało się pobrać statusów.", type: 'error' });
      } finally {
        setStatusesLoading(false); // Zakończ ładowanie statusów
      }
    };
    if (backendUrl) {
      fetchAllStatuses();
    }
  }, [backendUrl]); // Removed showPopup dependency, maybe not needed for statuses/templates

  // Pobieranie szablonów
  useEffect(() => {
    const fetchTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const url = `${backendUrl}/public/templates`;
        console.log("[useClientOrderData] fetchTemplates => GET =>", url);
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`Błąd pobierania szablonów (status=${res.status})`);
        }
        const data = await res.json();
        console.log("[useClientOrderData] fetchTemplates => data=", data);
        setTemplates(data || []); // Upewnij się, że data jest tablicą
      } catch (err) {
        console.error("[useClientOrderData] fetchTemplates =>", err);
        if (showPopup) {
          showPopup({ title: "Błąd", message: "Nie udało się pobrać listy dostępnych szablonów.", type: 'error' });
        }
        setTemplates([]); // W razie błędu ustaw pustą tablicę
      } finally {
        setTemplatesLoading(false);
      }
    };

    if (backendUrl) {
      fetchTemplates();
    }
  }, [backendUrl, showPopup]); // Added showPopup dependency

  // Usunięto useEffect sprawdzający personalizationAvailability

  return {
    order,
    setOrder, // Expose setOrder so actions hook can update it
    isLoading,
    statuses,
    statusesLoading,
    templates,
    templatesLoading,
    // Usunięto personalizationAvailability i personalizationLoading z zwracanych wartości
    fetchOrder // Expose fetchOrder for potential manual refresh
  };
}
